import {
  Channel,
  ChannelFilters,
  ErrorFromResponse,
  GetUnreadCountAPIResponse,
  LocalMessage,
  MessageResponse,
  StreamChat,
} from 'stream-chat';

import { User } from 'src/constants/dataTypes';
import { ChannelMembershipType } from 'src/store/messages/types';

export const canAccessMessageChannel = (
  channel: Channel,
  viewer: Pick<User, 'isClientAccessLimited' | 'companyAccessList'> | undefined,
  isClient: boolean,
): boolean => {
  if (isClient) return true;
  if (!viewer) return false;
  const companyId = channel.data?.companyID;
  return (
    !companyId ||
    !viewer.isClientAccessLimited ||
    Boolean(viewer.companyAccessList?.includes(companyId as string))
  );
};

export interface UnreadViewer {
  viewerGetstreamId: string | undefined;
  isInternalUser: boolean;
}

// GetStream error code 9 is "Too many requests" (HTTP 429).
const STREAM_RATE_LIMIT_ERROR_CODE = 9;

export const isStreamRateLimitError = (error: unknown): boolean =>
  error instanceof ErrorFromResponse &&
  error.code === STREAM_RATE_LIMIT_ERROR_CODE;

// GetStream returns code -1 on the QueryChannels body when the query times out
// ("query channels timed out"). The SDK wraps that body as ErrorFromResponse.
const STREAM_TIMEOUT_ERROR_CODE = -1;

// Widens the rate-limit guard to also cover query timeouts — both are
// transient, unlike a real Stream failure such as an auth error.
export const isStreamTransientError = (error: unknown): boolean =>
  isStreamRateLimitError(error) ||
  (error instanceof ErrorFromResponse &&
    error.code === STREAM_TIMEOUT_ERROR_CODE);

// Original failure plus two retries. Each retry remounts the channel list,
// which is what actually re-issues QueryChannels.
export const MAX_CHANNEL_QUERY_RECOVERY_ATTEMPTS = 2;
const CHANNEL_QUERY_RECOVERY_DELAY_MS = 800;

export type ChannelQueryRecovery =
  | { type: 'none' }
  | { type: 'retry'; delayMs: number }
  | { type: 'show-connection-error' };

// An empty inbox plus a transient QueryChannels failure renders a blank
// messages page: the list error slot is empty, and the page-level connection
// error is suppressed so a blip does not cover a loaded inbox. Retry while
// nothing is open, then show the connection error so the page cannot sit blank.
export const channelQueryRecoveryAction = ({
  error,
  hasActiveChannel,
  hasLoadedChannels,
  attemptsSoFar,
}: {
  error: unknown;
  hasActiveChannel: boolean;
  hasLoadedChannels: boolean;
  attemptsSoFar: number;
}): ChannelQueryRecovery => {
  // Mobile leaves the list up without selecting a conversation. A later
  // timeout must not retry over that list or replace it with the connection
  // error. Recovery is only for a first load that never produced channels.
  if (
    !error ||
    hasActiveChannel ||
    hasLoadedChannels ||
    !isStreamTransientError(error)
  ) {
    return { type: 'none' };
  }
  if (attemptsSoFar < MAX_CHANNEL_QUERY_RECOVERY_ATTEMPTS) {
    return {
      type: 'retry',
      delayMs: CHANNEL_QUERY_RECOVERY_DELAY_MS * (attemptsSoFar + 1),
    };
  }
  return { type: 'show-connection-error' };
};

// Mirrors backend `constants.GetstreamRoleAdmin` — assigned to internal users
// when their GetStream user is provisioned. Client users get role `"user"`.
const IU_GETSTREAM_ROLE = 'admin';
// GetStream returns 10 channels unless asked for more and allows up to 30 per query.
const SAME_COMPOSITION_QUERY_LIMIT = 30;
const UNREAD_CHANNEL_PAGE_LIMIT = 30;
const UNREAD_MESSAGE_PAGE_LIMIT = 100;
const MAX_UNREAD_CHANNELS_TO_INSPECT = 300;

type StreamMessage = LocalMessage | MessageResponse;
type ChannelWithUnreadPredicate = Channel & {
  _countMessageAsUnread?: (message: StreamMessage) => boolean;
};
type UnreadChannelSummary = GetUnreadCountAPIResponse['channels'][number] & {
  channel_type?: string;
};

export const toMessagingCid = (channelId: string) =>
  channelId.includes(':') ? channelId : `messaging:${channelId}`;

const getChannelUnreadMessagesInState = (
  channel: Channel,
  lastReadTime: number,
) =>
  (channel.state.latestMessages || channel.state.messages || []).filter(
    (message) => {
      const createdAt = message.created_at
        ? new Date(message.created_at).getTime()
        : 0;
      return createdAt > lastReadTime;
    },
  );

// `message.user.role` isn't always populated on the snapshot returned with a
// message — backend-sent messages (mass message, SES inbound, etc.) sometimes
// arrive with the role missing on the embedded user. Fall back to the channel
// members map, which is reliably hydrated by `queryChannels` and carries each
// member's authoritative role. Without this fallback, IU-authored mass messages
// slip past the IU-to-IU suppression filter on the receiving IU's badge.
const isMessageAuthorIU = (
  channel: Channel,
  authorId: string,
  message: StreamMessage,
): boolean => {
  if (message.user?.role === IU_GETSTREAM_ROLE) return true;
  const member = channel.state.members?.[authorId];
  return member?.user?.role === IU_GETSTREAM_ROLE;
};

const shouldCountMessageAsUnreadForViewer = (
  channel: Channel,
  message: StreamMessage,
  viewer: UnreadViewer,
): boolean => {
  const authorId = message.user?.id;
  if (!authorId) return false;
  if (authorId === viewer.viewerGetstreamId) return false;
  if (isMessageAuthorIU(channel, authorId, message)) return false;

  const streamShouldCount = (channel as ChannelWithUnreadPredicate)
    ._countMessageAsUnread;
  if (typeof streamShouldCount === 'function') {
    return streamShouldCount.call(channel, message);
  }

  if (message.shadowed || message.silent) return false;
  if (message.parent_id && !message.show_in_channel) return false;
  return true;
};

/**
 * Returns the unread message count for a channel from a given viewer's perspective.
 *
 * For client-user viewers: returns GetStream's native per-channel unread count.
 * For internal-user viewers: filters out messages authored by other IUs so that
 * IU-to-IU messages do not surface as unread. Respected exceptions:
 *   - Manual "mark as unread" (detected via `first_unread_message_id` on read
 *     state) always counts as unread.
 *   - Messages authored by clients (GetStream role `"user"`) always count.
 */
export const getDerivedChannelUnreadCount = (
  channel: Channel,
  viewer: UnreadViewer,
): number => {
  const rawCount = channel.countUnread();
  if (rawCount === 0) return 0;
  if (!viewer.isInternalUser || !viewer.viewerGetstreamId) {
    return rawCount;
  }

  const readState = channel.state.read?.[viewer.viewerGetstreamId];
  if (readState?.first_unread_message_id) {
    return rawCount;
  }

  const lastReadTime = readState?.last_read
    ? new Date(readState.last_read).getTime()
    : 0;
  const unreadMessagesInState = getChannelUnreadMessagesInState(
    channel,
    lastReadTime,
  );

  let count = 0;
  for (const message of unreadMessagesInState) {
    if (shouldCountMessageAsUnreadForViewer(channel, message, viewer)) {
      count += 1;
    }
  }

  if (unreadMessagesInState.length < rawCount) {
    return count > 0 ? count : rawCount;
  }

  return count;
};

export const isChannelUnreadForViewer = (
  channel: Channel,
  viewer: UnreadViewer,
): boolean => getDerivedChannelUnreadCount(channel, viewer) > 0;

const getUnreadCountResponse = async (
  chatClient: StreamChat,
  userId: string,
): Promise<GetUnreadCountAPIResponse> => chatClient.getUnreadCount(userId);

const getMessagingUnreadSummary = (
  response: GetUnreadCountAPIResponse,
): {
  channelIds: string[];
  unreadCount: number;
  channelTypesKnown: boolean;
} => {
  const unreadCount =
    response?.channel_type?.find(
      (channelType) => channelType.channel_type === 'messaging',
    )?.unread_count || 0;
  const channels = response.channels as UnreadChannelSummary[];
  const channelTypesKnown = channels.every(
    (channel) => typeof channel.channel_type === 'string',
  );

  return {
    channelIds: channels
      .filter((channel) =>
        channelTypesKnown ? channel.channel_type === 'messaging' : true,
      )
      .map((channel) => channel.channel_id),
    unreadCount,
    channelTypesKnown,
  };
};

const getPaginatedUnreadChannels = async (
  chatClient: StreamChat,
  channelIds: string[],
  enforceCandidateLimit: boolean,
): Promise<{ channels: Channel[]; hitLimit: boolean }> => {
  if (
    enforceCandidateLimit &&
    channelIds.length > MAX_UNREAD_CHANNELS_TO_INSPECT
  ) {
    return { channels: [], hitLimit: true };
  }

  const channels: Channel[] = [];

  for (
    let offset = 0;
    offset < channelIds.length;
    offset += UNREAD_CHANNEL_PAGE_LIMIT
  ) {
    const cids = channelIds
      .slice(offset, offset + UNREAD_CHANNEL_PAGE_LIMIT)
      .map(toMessagingCid);

    const page = await chatClient.queryChannels(
      {
        ...baseChannelsFilter,
        cid: {
          $in: cids,
        },
      } as ChannelFilters,
      {},
      {
        limit: UNREAD_CHANNEL_PAGE_LIMIT,
        message_limit: UNREAD_MESSAGE_PAGE_LIMIT,
      },
    );

    channels.push(...page);
    if (channels.length > MAX_UNREAD_CHANNELS_TO_INSPECT) {
      return { channels: [], hitLimit: true };
    }
  }

  return { channels, hitLimit: false };
};

/**
 * Computes the total messaging unread count for a viewer without relying on
 * the currently loaded channel list. IU viewers need per-message inspection to
 * suppress IU-authored messages, so this only queries unread channels and falls
 * back to GetStream's raw total when bounded inspection is incomplete.
 */
export const getDerivedTotalUnreadMessageCount = async (
  chatClient: StreamChat,
  viewer: UnreadViewer,
): Promise<number> => {
  if (!viewer.viewerGetstreamId) return 0;

  const unreadSummaryResponse = await getUnreadCountResponse(
    chatClient,
    viewer.viewerGetstreamId,
  );
  const {
    channelIds,
    unreadCount: rawTotal,
    channelTypesKnown,
  } = getMessagingUnreadSummary(unreadSummaryResponse);

  if (!viewer.isInternalUser) {
    return rawTotal;
  }

  if (rawTotal === 0) return 0;

  const { channels, hitLimit } = await getPaginatedUnreadChannels(
    chatClient,
    channelIds,
    channelTypesKnown,
  );
  if (hitLimit) return rawTotal;
  if (channels.length === 0) return rawTotal;

  // `queryChannels` above already hydrated each channel's `state.messages`
  // with up to `UNREAD_MESSAGE_PAGE_LIMIT` (100) latest messages, so we can
  // derive per-channel counts synchronously from in-state data instead of
  // round-tripping `channel.query()` once per channel. That cuts the initial
  // badge render from N+1 sequential requests to one batch.
  //
  // For the rare channel with more than 100 unread, `getDerivedChannelUnreadCount`
  // falls back to the channel's rawCount when its in-state window is too small
  // to filter — we accept that small over-count in exchange for instant render.
  // Cap by `rawTotal` as a safety bound.
  let total = 0;
  for (const channel of channels) {
    total += getDerivedChannelUnreadCount(channel, viewer);
  }

  return Math.min(total, rawTotal);
};

export const baseChannelsFilter: ChannelFilters = {
  type: 'messaging',
  disabled: false,
};

export const getMessagesChannels = async (
  chatClient: StreamChat,
  filter: any,
  options: { limit?: number } = {},
) => {
  const channels = await chatClient.queryChannels(
    {
      ...filter,
      type: 'messaging',
    },
    {},
    options,
  );
  return channels;
};

export const getChannelsWithUser = async (
  userData: { memberId: string; companyId: string | null },
  getstreamClient: StreamChat,
) => {
  const companyFilter = userData.companyId
    ? { companyID: userData.companyId }
    : {};
  const channelFilter = {
    ...baseChannelsFilter,
    members: {
      $in: [userData.memberId],
    },
    ...companyFilter,
  };
  return getstreamClient.queryChannels(channelFilter as ChannelFilters, {}, {});
};

export const getCompanyChannel = async (
  companyID: string,
  getstreamClient: StreamChat,
) => {
  const channelFilter = {
    ...baseChannelsFilter,
    companyID: `${companyID}`,
    membershipType: 'company',
  };
  const channels: Channel[] = await getstreamClient.queryChannels(
    channelFilter as ChannelFilters,
    {},
    {},
  );
  return channels;
};

export const findMessageChannelsWithSameComposition = async (
  chatClient: StreamChat,
  members: string[],
  membershipType: ChannelMembershipType,
  companyID: string | undefined,
) => {
  const predicate = members.map((memberId: string) => ({
    members: {
      $in: [memberId],
    },
    ...(companyID ? { companyID: { $eq: companyID } } : {}),
  }));
  const filter = {
    ...baseChannelsFilter,
    membershipType,
    $and: predicate,
  };

  const channels = await getMessagesChannels(chatClient, filter, {
    limit: SAME_COMPOSITION_QUERY_LIMIT,
  });
  if (membershipType !== 'group') {
    return channels;
  }
  // The query matches any group that contains the selected members, so a larger group
  // would hide a smaller one. Internal users join every group, so only clients decide a match.
  // Members beyond the loaded page cannot be checked, so a channel whose loaded members all
  // belong is kept rather than risk a second copy; one with an extra client never matches.
  return channels.filter((channel) =>
    Object.values(channel.state.members).every(
      (member) =>
        member.user?.role === IU_GETSTREAM_ROLE ||
        members.includes(member.user_id ?? ''),
    ),
  );
};
